import api from './api-service';
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';
import FlagService from './flag-service';
import UserService from '~/modules/services/user-service';

const STORE_KEY = 'pixwel.api.credentials';
const CLERK_STORE_KEY = '__session';

/**
 * Auth error message constants used when session resume/login fails.
 * Exported so other modules (e.g. route-service) can reference them
 * without duplicating magic strings.
 */
export const AUTH_ERRORS = {
	NO_TOKEN: 'no-token',
	TOKEN_EXPIRED: 'token-expired',
	ACCESS_DENIED: 'access-denied'
};

const AuthService = {

	_persistSessionToken: (session, username) => {
		if(session?.token) {
			AuthService.setToken({
				username: session.emailNormal || username,
				token: session.token
			});
		}
	},

	_configureAuthenticatedSession: (session) => {
		if( !session?._id ) {
			return session;
		}

		// Identify in every environment so person-targeted feature flags resolve
		// locally too. PostHog analytics capture is suppressed outside
		// production/staging in FlagService.init(), so this adds no dev noise.
		FlagService.identify(session);

		LogRocket?.identify(session._id, {
			name: session.name,
			email: session.emailNormal,
			isImpersonating: AuthService.getToken().isImpersonating || false
		});
		const isAdmin = UserService.isAdmin(session);
		const isImpersonating = AuthService.isImpersonating();
		if( Sentry.getCurrentHub().getClient() ) {
			Sentry.getCurrentHub().getClient()
				.getOptions().enabled = (window.pixwel?.env !== 'development' && (isAdmin || isImpersonating));
		}
		LogRocket.getSessionURL((sessionURL) => {
			document.cookie = `LogRocket=${sessionURL}`;
			Sentry.getCurrentScope().setExtra('sessionURL', sessionURL);
		});

		Sentry.getCurrentScope().setUser({
			email: session.emailNormal,
			username: session.name
		});

		return session;
	},

	login: (credentials) => {

		const isToken = !!credentials.token;
		const isTwoFactor = credentials.isTwoFactor;

		if(isToken && isTwoFactor) {
			AuthService.setToken({
				username: credentials.username,
				token: credentials.token
			});
		}

		credentials = !isToken
			? credentials
			: {
				username: credentials.username,
				password: 'token-' + credentials.token
			};

		const url = `${window.location.origin}/login/:token`;
		const params = {};
		if(!isTwoFactor) {
			params.url = url;
		}
		return api.client.get('/session', { params, auth: credentials })
			.then(({ data: session } = {}) => {
				if(!session) {
					throw new Error(isToken ? AUTH_ERRORS.TOKEN_EXPIRED : AUTH_ERRORS.ACCESS_DENIED);
				}

				// 2FA intermediate response (e.g. { twofactor: true }) - return early without persisting
				if( !session._id ) {
					return session;
				}

				AuthService._persistSessionToken(session, credentials.username);

				return AuthService._configureAuthenticatedSession(session);
			});
	},

	/**
	 * Exchanges a Google authorization code for an ordinary Pixwel session.
	 *
	 * The API redeems the code with Google, matches the resulting ID token's verified address
	 * against a user record, and mints the same session token a password login gets — so from
	 * here on nothing downstream (resume, impersonation, Aspera, logout) can tell them apart.
	 *
	 * @param {Object} params
	 * @param {string} params.code - the one-time code from Google's popup
	 * @returns {Promise<Object>} the session
	 */
	googleLogin: ({ code }) => {
		return api.client.post('/session/google', { code }, {
			// The shared response interceptor treats any 401 as an expired session: it clears
			// the cookie, toasts "please try logging in again" and reloads. That would wipe a
			// sign-in error before anyone could read it, so a refusal is read from the status
			// here instead of being thrown at the interceptor.
			validateStatus: () => true
		})
			.then(({ data, status } = {}) => {
				if(status !== 200 || !data?.token) {
					// A refusal carries a reason worth repeating; a 5xx carries an error code
					// meant for our logs (`google_auth_unconfigured`), not for the person.
					throw new Error(status >= 500
						? 'Google sign-in is not available right now'
						: (data?.error || AUTH_ERRORS.ACCESS_DENIED));
				}

				AuthService._persistSessionToken(data, data.emailNormal);

				return AuthService._configureAuthenticatedSession(data);
			});
	},

	guestLogin: ({ username, token }) => {
		return api.client.post('/session/guest', { username, token })
			.then(({ data: session } = {}) => {
				if(!session || !session.token) {
					throw new Error(AUTH_ERRORS.ACCESS_DENIED);
				}

				AuthService._persistSessionToken(session, username);
				return AuthService._configureAuthenticatedSession(session);
			});
	},

	logout: () => {
		return api.client.delete('/session').then((response) => {
			AuthService.removeToken();
			window.location.href='/';
			return response?.data || response || {};
		});
	},

	become: async(userId) => {
		try {
			const actual = AuthService.getToken();
			const result = await api.client.patch('/session', { _id: userId });

			if(!result?.data?.username || !result?.data?.token) {
				throw new Error('User not found');
			}

			const session = result?.data || {};
			await AuthService.removeToken();
			await AuthService.setToken({
				isImpersonating: true,
				actual,
				username: session.username,
				token: session.token
			});

			window.location.reload();
		}
		catch{
			window.location.reload();
		}
	},

	isImpersonating: () => {
		return AuthService.getToken().isImpersonating || false;
	},

	switchBack: async() => {
		const username = AuthService.getToken()?.actual?.username || null;
		const token = AuthService.getToken()?.actual?.token || null;

		if(username && token) {
			await AuthService.setToken({
				isImpersonating: false,
				username,
				token
			});
		}
		else {
			await AuthService.removeToken();
		}
		window.location.reload();
	},

	resetPassword: async({ token, password }) => {
		await api.client.post('/password-reset', { token, password });
	},

	requestResetPassword({ email, url }) {
		return api.client.post('/password-reset', {
			email,
			url: decodeURIComponent(url)
		});
	},

	twoFactorLogin: (token) => {
		return api.client.post('/session', { token });
	},

	removeToken: () => {
		Cookies.remove(STORE_KEY);
	},

	resumeSession: async() => {
		const token = AuthService.getToken();

		if(typeof token === 'string' && token.startsWith('ey')) {
			let session = await api.client
				.get('/session', {
					headers: {
						Authorization: token
					}
				})
				.then((res) => res.data);
			return session;
		}

		if(!token) {
			throw new Error(AUTH_ERRORS.NO_TOKEN);
		}

		return await AuthService.login({
			username: token.username,
			token: token.token
		});
	},
	isJWT(tok) {
		const token = tok || this.getToken();
		return typeof token === 'string' && token.startsWith('ey');
	},
	getToken: () => {
		const clerkToken = Cookies.getJSON(CLERK_STORE_KEY);
		if(clerkToken) {
			return clerkToken;
		}
		const token = Cookies.getJSON(STORE_KEY);
		if(!token || !token.username || !token.token) {
			return false;
		}
		return token;
	},
	setToken: (credentials) => {
		return Cookies.set(STORE_KEY, credentials);
	}
};

export default AuthService;
